<template>
  <div class="access-tree">
    <div class="access-tree-toolbar">
      <Input
        v-model="search"
        icon="magnifying-glass"
        icon-position="left"
        size="sm"
        classes="grow"
        placeholder="Search regions, orgs or dealerships"
      />
      <div class="flex gap-2 shrink-0">
        <Button
          v-if="allowSelectAll"
          :label="allSelected ? 'Clear all' : 'Select all'"
          link
          size="sm"
          @click="toggleSelectAll"
        />
        <Button
          label="Expand all"
          link
          size="sm"
          @click="expandAll"
        />
        <Button
          label="Collapse all"
          link
          size="sm"
          @click="collapseAll"
        />
      </div>
    </div>

    <div
      v-if="!visibleTree.length"
      class="access-tree-empty"
    >
      {{ tree.length ? 'Nothing matches that search.' : 'There are no dealerships available to assign.' }}
    </div>

    <ul
      v-else
      class="access-tree-list"
    >
      <li
        v-for="region in visibleTree"
        :key="region.id"
      >
        <div class="access-tree-row">
          <button
            type="button"
            class="access-tree-caret"
            :aria-label="isRegionExpanded(region.id) ? 'Collapse region' : 'Expand region'"
            @click="toggleRegionExpanded(region.id)"
          >
            <Icon :type="isRegionExpanded(region.id) ? 'chevron-down' : 'chevron-right'" />
          </button>
          <Checkbox
            size="sm"
            :is-checked="regionState(region) === 'checked'"
            :indeterminate="regionState(region) === 'partial'"
            @change="onRegionToggle(region)"
          />
          <div
            class="access-tree-label"
            @click="toggleRegionExpanded(region.id)"
          >
            <span class="font-medium">{{ region.name }}</span>
            <span class="access-tree-meta">{{ regionSummary(region) }}</span>
          </div>
        </div>

        <ul v-if="isRegionExpanded(region.id)">
          <li
            v-for="org in region.orgs"
            :key="org.id"
          >
            <div class="access-tree-row access-tree-row-org">
              <button
                type="button"
                class="access-tree-caret"
                :aria-label="isOrgExpanded(region.id, org.id) ? 'Collapse org' : 'Expand org'"
                @click="toggleOrgExpanded(region.id, org.id)"
              >
                <Icon :type="isOrgExpanded(region.id, org.id) ? 'chevron-down' : 'chevron-right'" />
              </button>
              <Checkbox
                size="sm"
                :is-checked="orgState(region, org) === 'checked'"
                :indeterminate="orgState(region, org) === 'partial'"
                @change="onOrgToggle(region, org)"
              />
              <div
                class="access-tree-label"
                @click="toggleOrgExpanded(region.id, org.id)"
              >
                <span>{{ org.name }}</span>
                <span
                  v-if="orgScoped(region, org)"
                  class="access-tree-tag access-tree-tag-brand"
                >
                  Whole org
                </span>
                <span class="access-tree-meta">{{ orgSummary(region, org) }}</span>
              </div>
            </div>

            <ul v-if="isOrgExpanded(region.id, org.id)">
              <li
                v-for="dealership in org.dealerships"
                :key="dealership.id"
              >
                <div class="access-tree-row access-tree-row-dealership">
                  <Checkbox
                    size="sm"
                    :is-checked="dealershipChecked(region, org, dealership)"
                    @change="onDealershipToggle(region, org, dealership)"
                  />
                  <div class="access-tree-label">
                    <span>{{ dealership.name }}</span>
                    <span
                      v-if="dealership.manufacturerName"
                      class="access-tree-meta"
                    >
                      {{ dealership.manufacturerName }}
                    </span>
                    <span
                      v-if="impliedIds.has(dealership.id)"
                      class="access-tree-tag"
                      title="Already covered by a brand grant"
                    >
                      Via brand
                    </span>
                  </div>
                </div>
              </li>
            </ul>
          </li>

          <!-- Sites with no org can only be granted one by one -->
          <li
            v-for="dealership in region.orphans"
            :key="`orphan-${dealership.id}`"
          >
            <div class="access-tree-row access-tree-row-org">
              <Checkbox
                size="sm"
                :is-checked="dealershipChecked(region, null, dealership)"
                @change="onDealershipToggle(region, null, dealership)"
              />
              <div class="access-tree-label">
                <span>{{ dealership.name }}</span>
                <span class="access-tree-meta">No org</span>
              </div>
            </div>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
// components
import Button from '@/components/button/Button.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import Icon from '@/components/icon/Icon.vue'
import Input from '@/components/forms/Input.vue'
// utilities
import {
  accessTreeOrgKey,
  buildAccessTree,
  countRegionDealerships,
  countSelectedOrgDealerships,
  filterAccessTree,
  getOrgCheckState,
  getRegionCheckState,
  indexAccessTree,
  isDealershipChecked,
  isOrgScoped,
  toggleDealership,
  toggleOrg,
  toggleRegion
} from '@/utilities/access/accessTree'
// types
import type {
  AccessCheckState,
  AccessTreeDealership,
  AccessTreeOrg,
  AccessTreeRegion,
  AccessTreeSelection,
  ScopableOrgIds
} from '@/utilities/access/accessTree'
import type { AccessDealership } from '@/utilities/access/accessGroupings'

const props = withDefaults(
  defineProps<{
    /** The dealerships the session user is allowed to hand out */
    dealerships: AccessDealership[]
    /** The org and dealership scopes currently granted */
    modelValue: AccessTreeSelection
    /** Dealerships already covered by a grant made outside the tree */
    impliedDealershipIds?: number[]
    /** Orgs the session user holds outright, or null when they hold all access */
    scopableOrgIds?: ScopableOrgIds
    /** Shows a select all / clear all shortcut in the toolbar */
    allowSelectAll?: boolean
  }>(),
  {
    impliedDealershipIds: () => [],
    scopableOrgIds: null,
    allowSelectAll: false
  }
)

const emit = defineEmits<{ 'update:modelValue': [AccessTreeSelection] }>()

const search = ref('')
const expandedRegions = ref<string[]>([])
const expandedOrgs = ref<string[]>([])

/** The full tree, used for every checkbox state so searching can't skew it */
const tree = computed(() => buildAccessTree(props.dealerships))

/** Lookups from a pruned branch back to its full counterpart */
const index = computed(() => indexAccessTree(tree.value))

/** The tree as rendered, narrowed by the search term */
const visibleTree = computed(() => filterAccessTree(tree.value, search.value))

const impliedIds = computed(() => new Set(props.impliedDealershipIds))

/**
 * Resolve a rendered region branch back to its full, unpruned branch.
 *
 * @param region - The rendered region branch
 * @returns The full region branch
 */
function fullRegion(region: AccessTreeRegion): AccessTreeRegion {
  return index.value.regions.get(region.id) ?? region
}

/**
 * Resolve a rendered org branch back to its full, unpruned branch.
 *
 * @param region - The rendered region branch the org sits in
 * @param org - The rendered org branch
 * @returns The full org branch
 */
function fullOrg(region: AccessTreeRegion, org: AccessTreeOrg): AccessTreeOrg {
  return index.value.orgs.get(accessTreeOrgKey(region.id, org.id)) ?? org
}

/**
 * Checkbox state of a region row.
 *
 * @param region - The rendered region branch
 * @returns The tri-state value
 */
function regionState(region: AccessTreeRegion): AccessCheckState {
  return getRegionCheckState(fullRegion(region), props.modelValue)
}

/**
 * Checkbox state of an org row.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch
 * @returns The tri-state value
 */
function orgState(region: AccessTreeRegion, org: AccessTreeOrg): AccessCheckState {
  return getOrgCheckState(fullOrg(region, org), props.modelValue)
}

/**
 * Whether an org is granted as a whole rather than site by site.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch
 * @returns True when an org scope covers it
 */
function orgScoped(region: AccessTreeRegion, org: AccessTreeOrg): boolean {
  return isOrgScoped(fullOrg(region, org), props.modelValue)
}

/**
 * Whether a site's checkbox reads as ticked.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch, or null for a site with no org
 * @param dealership - The site row
 * @returns True when the site is covered
 */
function dealershipChecked(
  region: AccessTreeRegion,
  org: AccessTreeOrg | null,
  dealership: AccessTreeDealership
): boolean {
  return isDealershipChecked(dealership, org ? fullOrg(region, org) : null, props.modelValue)
}

/**
 * Summary line for a region row.
 *
 * @param region - The rendered region branch
 * @returns A count of orgs and sites
 */
function regionSummary(region: AccessTreeRegion): string {
  const full = fullRegion(region)
  const orgCount = full.orgs.length
  const siteCount = countRegionDealerships(full)

  return `${orgCount} org${orgCount === 1 ? '' : 's'} · ${siteCount} site${siteCount === 1 ? '' : 's'}`
}

/**
 * Summary line for an org row, spelling out how much of it is selected.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch
 * @returns A site count, or a selected/total count when picked site by site
 */
function orgSummary(region: AccessTreeRegion, org: AccessTreeOrg): string {
  const full = fullOrg(region, org)
  const total = full.dealerships.length

  // A whole-org scope needs no breakdown, it covers future sites too
  if (isOrgScoped(full, props.modelValue)) {
    return `${total} site${total === 1 ? '' : 's'}`
  }

  const selected = countSelectedOrgDealerships(full, props.modelValue)

  // Nothing picked individually shows the plain size of the org
  if (selected === 0) {
    return `${total} site${total === 1 ? '' : 's'}`
  }

  return `${selected} of ${total} sites`
}

/**
 * Whether a region branch is expanded.
 *
 * @param regionId - The region branch ID
 * @returns True when expanded
 */
function isRegionExpanded(regionId: string): boolean {
  return expandedRegions.value.includes(regionId)
}

/**
 * Whether an org branch is expanded.
 *
 * @param regionId - The region branch ID
 * @param orgId - The org ID
 * @returns True when expanded
 */
function isOrgExpanded(regionId: string, orgId: number): boolean {
  return expandedOrgs.value.includes(accessTreeOrgKey(regionId, orgId))
}

/**
 * Expand or collapse a region branch.
 *
 * @param regionId - The region branch ID
 */
function toggleRegionExpanded(regionId: string): void {
  expandedRegions.value = isRegionExpanded(regionId)
    ? expandedRegions.value.filter(id => id !== regionId)
    : [...expandedRegions.value, regionId]
}

/**
 * Expand or collapse an org branch.
 *
 * @param regionId - The region branch ID
 * @param orgId - The org ID
 */
function toggleOrgExpanded(regionId: string, orgId: number): void {
  const key = accessTreeOrgKey(regionId, orgId)

  expandedOrgs.value = isOrgExpanded(regionId, orgId)
    ? expandedOrgs.value.filter(id => id !== key)
    : [...expandedOrgs.value, key]
}

/** Whether every branch in the tree is fully ticked */
const allSelected = computed(
  () => tree.value.length > 0 && tree.value.every(region => regionState(region) === 'checked')
)

/** Tick every branch in the tree, or clear the selection when it's already full */
function toggleSelectAll(): void {
  // Clearing is simply an empty selection
  if (allSelected.value) {
    emit('update:modelValue', { orgs: [], dealerships: [] })
    return
  }

  let next = props.modelValue
  for (const region of tree.value) {
    // Toggling a region that's already full would clear it again
    if (getRegionCheckState(region, next) !== 'checked') {
      next = toggleRegion(region, next, props.scopableOrgIds)
    }
  }

  emit('update:modelValue', next)
}

/** Expand every region and org in the tree */
function expandAll(): void {
  expandedRegions.value = tree.value.map(region => region.id)
  expandedOrgs.value = tree.value.flatMap(region => region.orgs.map(org => accessTreeOrgKey(region.id, org.id)))
}

/** Collapse the whole tree */
function collapseAll(): void {
  expandedRegions.value = []
  expandedOrgs.value = []
}

/**
 * Toggle a whole region, granting each of its orgs as a scope.
 *
 * @param region - The rendered region branch
 */
function onRegionToggle(region: AccessTreeRegion): void {
  emit('update:modelValue', toggleRegion(fullRegion(region), props.modelValue, props.scopableOrgIds))
}

/**
 * Toggle a whole org, storing it as an org scope.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch
 */
function onOrgToggle(region: AccessTreeRegion, org: AccessTreeOrg): void {
  emit('update:modelValue', toggleOrg(fullOrg(region, org), props.modelValue, props.scopableOrgIds))
}

/**
 * Toggle a single site.
 *
 * @param region - The rendered region branch
 * @param org - The rendered org branch, or null for a site with no org
 * @param dealership - The site row
 */
function onDealershipToggle(
  region: AccessTreeRegion,
  org: AccessTreeOrg | null,
  dealership: AccessTreeDealership
): void {
  emit('update:modelValue', toggleDealership(dealership, org ? fullOrg(region, org) : null, props.modelValue))
}

// Open the branches that hold search matches so results aren't hidden
watch(search, term => {
  // Clearing the search leaves the tree as the user left it
  if (!term.trim()) {
    return
  }

  expandedRegions.value = visibleTree.value.map(region => region.id)
  expandedOrgs.value = visibleTree.value.flatMap(region =>
    region.orgs.map(org => accessTreeOrgKey(region.id, org.id))
  )
})

// Start with regions open, orgs closed, so the list stays scannable
watch(
  tree,
  branches => {
    // Only seed the expanded state while the user hasn't opened anything
    if (expandedRegions.value.length || !branches.length) {
      return
    }

    expandedRegions.value = branches.map(region => region.id)
  },
  { immediate: true }
)
</script>

<style scoped>
.access-tree {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  overflow: hidden;
}

.access-tree-toolbar {
  display: flex;
  align-items: center;
  gap: var(--s-2) var(--s-3);
  justify-content: space-between;
  flex-wrap: wrap;
  padding: var(--s-3);
  border-bottom: 1px solid var(--border-secondary);
  background-color: var(--bg-secondary_alt);
}

.access-tree-empty {
  padding: var(--s-4);
  color: var(--text-tertiary);
  font-size: var(--text-sm);
}

.access-tree-list {
  max-height: 420px;
  overflow-y: auto;
}

.access-tree-row {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  padding: var(--s-2) var(--s-3);
  border-bottom: 1px solid var(--border-secondary);
}

.access-tree-row:hover {
  background-color: var(--bg-secondary_alt);
}

.access-tree-row-org {
  padding-left: var(--s-7);
}

.access-tree-row-dealership {
  padding-left: var(--s-10);
}

.access-tree-caret {
  color: var(--text-quaternary);
  width: 16px;
  cursor: pointer;
}

.access-tree-label {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  flex-wrap: wrap;
  cursor: pointer;
  font-size: var(--text-sm);
  user-select: none;
}

.access-tree-meta {
  color: var(--text-tertiary);
  font-size: var(--text-xs);
}

.access-tree-tag {
  font-size: var(--text-xs);
  padding: 0 var(--s-2);
  border-radius: var(--rounded-full);
  border: 1px solid var(--border-secondary);
  color: var(--text-tertiary);
}

.access-tree-tag-brand {
  border-color: var(--fg-brand);
  color: var(--fg-brand);
}
</style>
